import LanguageSelector from '../i18n/LanguageSelector';
import { useAppTranslation } from '../i18n/useAppTranslation';
import MobileDownload from "../components/MobileDownload";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Drawer,
  IconButton,
} from "@mui/material";
import {
  ArrowForward,
  ArrowOutward,
  ArrowDownward,
  Check,
  Close,
  Menu,
  PlayArrow,
  Pause,
  FitnessCenter,
  CalendarMonth,
  TrendingUp,
  Forum,
  Bolt,
  SportsScore,
  People,
  East,
  Add,
} from "@mui/icons-material";
import {
  motion,
  MotionConfig,
  useReducedMotion,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { isNativeApp } from "../utils/platform";
import { usePageMeta } from "../utils/usePageMeta";
import "./LandingPage.css";

type Role = "SPORTIF" | "COACH";
type Preview = "planning" | "progression" | "messages";
// Reveal once without hiding content when JavaScript or motion is unavailable.
function useLandingReveal(root: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (media.matches || !root.current || !("IntersectionObserver" in window))
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("landing-revealed");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12 }
    );
    root.current
      .querySelectorAll(
        ".section-heading, .step-card, .platform-copy, .product-preview, .feature-card, .price-card, .mobile-download, .final-cta"
      )
      .forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [root]);
}

function PerformanceScene() {
  const { tr } = useAppTranslation();
  const reducedMotion = useReducedMotion();
  const [motionChoice, setMotionChoice] = useState<boolean | null>(null);
  const active = motionChoice ?? !reducedMotion;
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(x, { stiffness: 80, damping: 24 });
  const rotateY = useSpring(y, { stiffness: 80, damping: 24 });
  const reset = () => {
    x.set(0);
    y.set(0);
  };
  return (
    <div
      className={`performance-scene ${
        active ? "performance-playing" : "performance-paused"
      }`}
      onPointerMove={(event) => {
        if (!active || event.pointerType !== "mouse") return;
        const bounds = event.currentTarget.getBoundingClientRect();
        x.set(-((event.clientY - bounds.top) / bounds.height - 0.5) * 3);
        y.set(((event.clientX - bounds.left) / bounds.width - 0.5) * 4);
      }}
      onPointerLeave={reset}
    >
      <div className="performance-halo" aria-hidden="true" />
      <div className="performance-lanes" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
      <div className="performance-caption">
        <span>G1SPORT / PERFORMANCE</span>
        <span>{tr("APERÇU DU SUIVI")}</span>
      </div>
      <motion.div
        className="performance-console"
        style={{ rotateX: active ? rotateX : 0, rotateY: active ? rotateY : 0 }}
      >
        <div className="performance-header">
          <div>
            <span className="performance-kicker">{tr("TABLEAU DE BORD SPORTIF")}</span>
            <h3>{tr("Chaque effort. Un progrès.")}</h3>
          </div>
          <span className="performance-monogram">
            <img
              className="app-brand-logo"
              src="/g1s-logo-3d.png"
              alt=""
              width="44"
              height="56"
            />
          </span>
        </div>
        <div className="performance-overview">
          <div className="performance-score">
            <span>{tr("Score global")}</span>
            <strong>
              8,4<small>/10</small>
            </strong>
            <p>
              <TrendingUp fontSize="small" />  {tr("+1,2 point sur la période")} </p>
          </div>
          <div
            className="performance-ring"
            role="img"
            aria-label={tr("Exemple de score global : 8,4 sur 10")}
          >
            <svg viewBox="0 0 140 140" aria-hidden="true">
              <circle cx="70" cy="70" r="58" className="ring-base" />
              <circle
                cx="70"
                cy="70"
                r="58"
                className="ring-value"
                pathLength="100"
              />
            </svg>
            <div>
              <SportsScore />
              <span>{tr("PROGRESSION")}</span>
            </div>
          </div>
        </div>
        <div className="performance-chart-label">
          <span>{tr("Évolution des évaluations")}</span>
          <span>{tr("6 dernières séances")}</span>
        </div>
        <div
          className="performance-graph"
          role="img"
          aria-label={tr("Exemple de progression sur six évaluations, de 5,5 à 8,4 sur 10")}
        >
          <svg viewBox="0 0 480 160" preserveAspectRatio="none">
            <defs>
              <linearGradient id="performance-area" x1="0" y1="0" x2="0" y2="1">
                <stop stopColor="#d7bc83" stopOpacity=".2" />
                <stop offset="1" stopColor="#d7bc83" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d="M0 136C35 136 53 102 96 106S153 127 192 92S247 87 288 62S344 77 384 40S451 49 480 12V160H0Z"
              fill="url(#performance-area)"
            />
            <path
              className="performance-curve"
              d="M0 136C35 136 53 102 96 106S153 127 192 92S247 87 288 62S344 77 384 40S451 49 480 12"
              stroke="#d7bc83"
              strokeWidth="2.5"
              fill="none"
            />
            <circle
              className="performance-beacon"
              cx="478"
              cy="13"
              r="4"
              fill="#f3ddb0"
            />
          </svg>
        </div>
        <div className="performance-chart-axis" aria-hidden="true">
          {["01", "02", "03", "04", "05", "06"].map((label) => (
            <span key={tr(label)}>{tr(label)}</span>
          ))}
        </div>
        <div className="performance-metrics">
          {[
            { label: tr("Physique"), value: "8,5", width: "85%" },
            { label: tr("Technique"), value: "7,8", width: "78%" },
            { label: tr("Mental"), value: "9,0", width: "90%" },
          ].map((metric) => (
            <div key={tr(metric.label)}>
              <span>{tr(metric.label)}</span>
              <strong>
                {metric.value}
                <small>/10</small>
              </strong>
              <div className="metric-track">
                <i style={{ width: metric.width }} />
              </div>
            </div>
          ))}
        </div>
      </motion.div>
      <div className="performance-session">
        <span className="performance-session-icon">
          <CalendarMonth />
        </span>
        <div>
          <span>{tr("VOTRE PROCHAINE ÉTAPE")}</span>
          <strong>{tr("Préparation physique")}</strong>
          <small>{tr("Programme personnalisé · 60 min")}</small>
        </div>
        <span className="performance-session-status">
          <Check fontSize="small" />  {tr("Planifiée")} </span>
      </div>
      <div className="performance-bottom">
        <span>{tr("Illustration de la plateforme · Données fictives")}</span>
        <button
          type="button"
          onClick={() => {
            reset();
            setMotionChoice(!active);
          }}
          aria-pressed={active}
          title={
            active
              ? tr("Mettre les animations en pause")
              : tr("Activer les animations de cet aperçu")
          }
          aria-label={
            active ? tr("Mettre les animations en pause") : tr("Activer les animations")
          }
        >
          {active ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
          <span>{active ? "Pause" : tr("Animer l’aperçu")}</span>
        </button>
      </div>
    </div>
  );
}

function ProductPreview({
  role,
  tab,
  setTab,
}: {
  role: Role;
  tab: Preview;
  setTab: (tab: Preview) => void;
}) {
  const { tr, locale } = useAppTranslation();
  const tabs: { id: Preview; label: string; icon: React.ReactNode }[] = [
    { id: "planning", label: "Planning", icon: <CalendarMonth /> },
    { id: "progression", label: "Progression", icon: <TrendingUp /> },
    { id: "messages", label: "Messages", icon: <Forum /> },
  ];
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  return (
    <div className="product-preview">
      <div className="preview-top">
        <div className="preview-brand">
          G1<span>SPORT</span>
        </div>
        <span className="preview-demo">{tr("Aperçu · données fictives")}</span>
        <span className="preview-avatar">{role === "COACH" ? "JD" : "AL"}</span>
      </div>
      <div className="preview-body">
        <div className="preview-greeting">
          <div>
            <small>{tr("VOTRE ESPACE")} {role}</small>
            <h3>
              {role === "COACH"
                ? "Faites grandir leur potentiel."
                : tr("Prêt à vous dépasser, Alex ?")}
            </h3>
          </div>
          <Bolt />
        </div>
        <div
          className="preview-tabs"
          role="tablist"
          aria-label={tr("Fonctionnalités à découvrir")}
        >
          {tabs.map((item, index) => (
            <button
              key={item.id}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              type="button"
              id={`tab-${item.id}`}
              role="tab"
              aria-selected={tab === item.id}
              aria-controls="preview-panel"
              tabIndex={tab === item.id ? 0 : -1}
              onClick={() => setTab(item.id)}
              onKeyDown={(event) => {
                if (
                  !["ArrowLeft", "ArrowRight", "Home", "End"].includes(
                    event.key
                  )
                )
                  return;
                event.preventDefault();
                const next =
                  event.key === "Home"
                    ? 0
                    : event.key === "End"
                    ? 2
                    : (index + (event.key === "ArrowRight" ? 1 : -1) + 3) % 3;
                setTab(tabs[next].id);
                tabRefs.current[next]?.focus();
              }}
            >
              {item.icon}
              {tr(item.label)}
            </button>
          ))}
        </div>
        <div
          id="preview-panel"
          className="preview-panel"
          role="tabpanel"
          aria-labelledby={`tab-${tab}`}
          tabIndex={0}
        >
          {tab === "planning" && (
            <>
              <div className="preview-week">
                {["L", "M", "M", "J", "V", "S", "D"].map((day, index) => (
                  <div className={index === 2 ? "selected" : ""} key={index}>
                    <small>{day}</small>
                    <strong>{12 + index}</strong>
                  </div>
                ))}
              </div>
              <div className="session-row">
                <span className="session-icon">
                  <FitnessCenter />
                </span>
                <div>
                  <strong>{tr("Renforcement musculaire")}</strong>
                  <small>
                    {role === "COACH"
                      ? tr("Avec Alex L. · Séance individuelle")
                      : tr("Avec votre coach · Programme personnalisé")}
                  </small>
                </div>
                <span className="session-time">
                  10:00<small>45 min</small>
                </span>
              </div>
              <div className="session-row">
                <span className="session-icon green">
                  <SportsScore />
                </span>
                <div>
                  <strong>{tr("Préparation physique")}</strong>
                  <small>
                    {role === "COACH"
                      ? tr("Avec Thomas M. · Séance individuelle")
                      : tr("Endurance & mobilité")}
                  </small>
                </div>
                <span className="session-time">
                  17:30<small>60 min</small>
                </span>
              </div>
              <div className="preview-note">
                <span className="status-dot" />  {tr("Vos séances, au même endroit.")} </div>
            </>
          )}
          {tab === "progression" && (
            <>
              <div className="progress-heading">
                <span>
                  {role === "COACH"
                    ? tr("Évolution de votre sportif")
                    : tr("Votre évolution")}
                  <small>{tr("Score global · 6 dernières évaluations")}</small>
                </span>
                <strong>
                  8,4<span>/10</span>
                </strong>
              </div>
              <div
                className="preview-chart"
                role="img"
                aria-label={tr("Exemple de progression du score global de 5,5 à 8,4 sur 10")}
              >
                <svg viewBox="0 0 480 140" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="chart-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop stopColor="#d7bc83" stopOpacity=".28" />
                      <stop offset="1" stopColor="#d7bc83" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0 120L90 95L185 105L280 57L380 45L480 15V140H0Z"
                    fill="url(#chart-fill)"
                  />
                  <path
                    d="M0 120L90 95L185 105L280 57L380 45L480 15"
                    stroke="#d7bc83"
                    strokeWidth="4"
                    fill="none"
                  />
                </svg>
              </div>
              <div className="progress-axes">
                {[tr("Physique"), tr("Technique"), tr("Mental"), tr("Volonté")].map(
                  (axis, i) => (
                    <div key={axis}>
                      <span>{axis}</span>
                      <strong>
                        {[8.5, 7.8, 9, 8.3][i].toLocaleString(locale)}
                        <small>/10</small>
                      </strong>
                    </div>
                  )
                )}
              </div>
            </>
          )}
          {tab === "messages" && (
            <div className="preview-conversation">
              <div className="message-contact">
                <span className="preview-avatar">
                  {role === "COACH" ? "AL" : "JD"}
                </span>
                <div>
                  <strong>
                    {role === "COACH" ? "Alex · Sportif" : "Julien · Coach"}
                  </strong>
                  <small>{tr("Votre accompagnement continue ici")}</small>
                </div>
              </div>
              <p className="message-bubble">
                 {tr("Très belle séance ! On garde le cap pour la prochaine ?")} </p>
              <p className="message-bubble outgoing">
                 {tr("Oui, motivé ! On travaille quoi cette semaine ?")} </p>
              <p className="message-bubble">
                 {tr("Endurance et renforcement. Le programme est prêt 💪")} </p>
              <small className="message-caption">
                 {tr("Échangez directement, avant et après vos séances.")} </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { tr } = useAppTranslation();
  const landingRef = useRef<HTMLDivElement>(null);
  useLandingReveal(landingRef);
  usePageMeta(
    "G1Sport — Ensemble, passez au niveau supérieur",
    "Trouvez votre coach, réservez vos séances et suivez votre progression. G1Sport rapproche coachs et sportifs, à chaque étape."
  );
  const navigate = useNavigate();
  const native = isNativeApp();
  const [role, setRole] = useState<Role>("SPORTIF");
  const [tab, setTab] = useState<Preview>("planning");
  const [menuOpen, setMenuOpen] = useState(false);
  const register = (selectedRole: Role = role) =>
    navigate("/register", { state: { role: selectedRole } });
  const openDemo = () => navigate(`/visiteur?profil=${role.toLowerCase()}`);
  const nav = [
    { label: "Le concept", href: "#concept" },
    { label: "La plateforme", href: "#plateforme" },
    ...(!native ? [{ label: "Les tarifs", href: "#tarifs" }] : []),
    ...(!native ? [{ label: "Sur mobile", href: "#mobile" }] : []),
    { label: "Vos questions", href: "#questions" },
  ];
  return (
    <MotionConfig reducedMotion="user">
      <div className="landing" ref={landingRef}>
        <a className="skip-link" href="#contenu">
           {tr("Aller au contenu")} </a>
        <header className="landing-header">
          <div className="landing-shell nav-inner">
            <Link to="/" className="landing-logo" aria-label="G1Sport, accueil">
              <img
                className="app-brand-logo"
                src="/g1s-logo-3d.png"
                alt=""
                width="44"
                height="56"
              />

              <span>
                G1SPORT<span className="logo-period">.</span>
              </span>
            </Link>
            <nav aria-label="Navigation principale" className="desktop-nav">
              {nav.map((item) => (
                <a href={item.href} key={item.href}>
                  {tr(item.label)}
                </a>
              ))}
            </nav>
            <div className="nav-actions"><LanguageSelector/>
              <Link className="login-link" to="/login">
                 {tr("Connexion")} </Link>
              <button
                className="landing-button compact"
                onClick={() => register()}
              >
                 {tr("Commencer")} <ArrowOutward fontSize="small" />
              </button>
              <button
                className="mobile-menu"
                aria-label={tr("Ouvrir le menu")}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(true)}
              >
                <Menu />
              </button>
            </div>
          </div>
        </header>
        <main id="contenu">
          <section className="landing-shell hero">
            <div className="hero-copy">
              <div className="eyebrow">
                <span className="status-dot" />  {tr("LE COACHING SPORTIF, AVEC EXIGENCE")} </div>
              <h1>
                 {tr("Votre ambition.")} <br />
                 {tr("Notre exigence.")} <br />
                <span>{tr("La performance.")}</span>
              </h1>
              <p className="hero-description">
                 {tr("Un accompagnement à la hauteur de vos objectifs. Connectez-vous au bon coach, structurez vos séances et mesurez votre progression.")} </p>
              <div
                className="role-switch"
                role="group"
                aria-label={tr("Votre profil")}
              >
                <button
                  aria-pressed={role === "SPORTIF"}
                  onClick={() => setRole("SPORTIF")}
                >
                  <SportsScore fontSize="small" />  {tr("Je suis sportif")} </button>
                <button
                  aria-pressed={role === "COACH"}
                  onClick={() => setRole("COACH")}
                >
                  <People fontSize="small" />  {tr("Je suis coach")} </button>
              </div>
              <div className="hero-actions">
                <button className="landing-button" onClick={() => role === "SPORTIF" ? navigate("/visiteur?profil=sportif&section=coachs") : register()}>
                  {role === "SPORTIF"
                    ? tr("Trouver mon coach")
                    : tr("Développer mon activité")}
                  <ArrowUpRight />
                </button>
                <button className="demo-button" onClick={openDemo}>
                  <span>
                    <PlayArrow fontSize="small" />
                  </span>
                   {tr("Accès visiteur")} </button>
              </div>
              <p className="hero-reassurance">
                <Check fontSize="small" />
                {native
                  ? tr("Coachs & sportifs. Un même esprit d’équipe.")
                  : role === "SPORTIF"
                  ? tr("Compte sportif gratuit. À vous de jouer.")
                  : "6 mois offerts, puis 19,90 € / mois."}
              </p>
              <div className="hero-footnote">
                <span>{tr("01 — CONNECTER")}</span>
                <span>{tr("02 — S’ENTRAÎNER")}</span>
                <span>{tr("03 — PROGRESSER")}</span>
              </div>
            </div>
            <PerformanceScene />
          </section>
          <div
            className="sports-strip"
            aria-label={tr("Pour toutes vos disciplines")}
          >
            <div className="landing-shell">
              <span>{tr("À CHACUN SON TERRAIN.")}</span>
              {["MUSCULATION", "RUNNING", "FOOTBALL", "BOXE", "FITNESS"].map(
                (sport) => (
                  <span key={tr(sport)}>
                    <span className="sport-asterisk">✳</span>
                    {tr(sport)}
                  </span>
                )
              )}
            </div>
          </div>
          <section className="landing-shell section-space" id="concept">
            <div className="section-heading">
              <div>
                <span className="eyebrow">{tr("L’ÉLAN QU’IL VOUS MANQUAIT")}</span>
                <h2>
                   {tr("Un objectif. Une équipe.")} <br />
                  <span>{tr("Votre prochaine version.")}</span>
                </h2>
              </div>
              <p>
                 {tr("Du premier contact à votre prochain record, G1Sport simplifie tout ce qui vous relie à votre coach.")} </p>
            </div>
            <div className="steps-grid">
              {[
                {
                  number: "01",
                  icon: <People />,
                  title: tr("La bonne rencontre."),
                  text: tr("Découvrez les profils, les spécialités et les parcours des coachs. Trouvez celui qui partage votre ambition."),
                },
                {
                  number: "02",
                  icon: <CalendarMonth />,
                  title: tr("Place à l’action."),
                  text: tr("Choisissez un programme et un créneau disponible. Votre planning et vos échanges restent à portée de main."),
                },
                {
                  number: "03",
                  icon: <TrendingUp />,
                  title: tr("Des progrès concrets."),
                  text: tr("Retrouvez vos évaluations, mesurez votre évolution et construisez la suite avec votre coach."),
                },
              ].map((step) => (
                <article className="step-card" key={step.number}>
                  <div className="step-top">
                    <span>{step.icon}</span>
                    <span>{step.number}</span>
                  </div>
                  <h3>{tr(step.title)}</h3>
                  <p>{tr(step.text)}</p>
                </article>
              ))}
            </div>
          </section>
          <section className="platform-section" id="plateforme">
            <div className="landing-shell platform-grid">
              <div className="platform-copy">
                <span className="eyebrow">
                   {tr("MOINS D’ORGANISATION. PLUS DE SPORT.")} </span>
                <h2>
                   {tr("Tout votre sport.")} <br />
                  <span>{tr("Un seul espace.")}</span>
                </h2>
                <p>
                  {role === "SPORTIF"
                    ? tr("Réservez, échangez, progressez. Votre relation avec votre coach continue bien au-delà de la séance.")
                    : tr("Votre planning, vos sportifs, leurs résultats. Gardez une vision claire de votre activité et du temps pour coacher.")}
                </p>
                <div
                  className="role-switch dark"
                  role="group"
                  aria-label={tr("Profil de l’aperçu")}
                >
                  <button
                    aria-pressed={role === "SPORTIF"}
                    onClick={() => setRole("SPORTIF")}
                  >
                     {tr("Espace sportif")} </button>
                  <button
                    aria-pressed={role === "COACH"}
                    onClick={() => setRole("COACH")}
                  >
                     {tr("Espace coach")} </button>
                </div>
                <ul className="platform-benefits">
                  {(role === "SPORTIF"
                    ? [
                        tr("Réservez sur les créneaux de votre coach"),
                        tr("Retrouvez vos programmes et évaluations"),
                        tr("Gardez le contact avec la messagerie"),
                      ]
                    : [
                        tr("Organisez vos disponibilités et séances"),
                        "Invitez et accompagnez vos sportifs",
                        tr("Évaluez leur progression sur plusieurs axes"),
                      ]
                  ).map((text) => (
                    <li key={tr(text)}>
                      <Check fontSize="small" />
                      {tr(text)}
                    </li>
                  ))}
                </ul>
                <button className="text-link light" onClick={openDemo}>
                   {tr("Tester la plateforme")} <ArrowForward fontSize="small" />
                </button>
              </div>
              <ProductPreview role={role} tab={tab} setTab={setTab} />
            </div>
          </section>
          <section className="landing-shell section-space features-section">
            <div className="section-heading">
              <div>
                <span className="eyebrow">{tr("PENSÉ POUR LE TERRAIN")}</span>
                <h2>
                   {tr("Le bon outil.")} <br />
                  <span>{tr("Au bon moment.")}</span>
                </h2>
              </div>
              <p>
                 {tr("Sportif du dimanche ou coach au quotidien : des outils utiles, sans compliquer votre pratique.")} </p>
            </div>
            <div className="features-grid">
              {[
                {
                  icon: <FitnessCenter />,
                  title: tr("Un programme qui vous ressemble"),
                  text: tr("Des programmes créés par votre coach, adaptés à vos objectifs et associés à vos séances."),
                  tag: tr("PERSONNALISATION"),
                },
                {
                  icon: <TrendingUp />,
                  title: tr("Chaque progrès a sa place"),
                  text: tr("Physique, technique, mental, volonté : suivez vos évaluations et votre score global dans le temps."),
                  tag: tr("PERFORMANCE"),
                },
                {
                  icon: <Forum />,
                  title: tr("Le lien fait la différence"),
                  text: tr("Une question, un encouragement, un ajustement ? Retrouvez vos conversations au même endroit."),
                  tag: tr("ACCOMPAGNEMENT"),
                },
                {
                  icon: <CalendarMonth />,
                  title: tr("Votre prochain rendez-vous"),
                  text: tr("Créneaux disponibles, demandes de réservation et statut des séances : gardez le rythme."),
                  tag: tr("ORGANISATION"),
                },
              ].map((feature, index) => (
                <article className="feature-card" key={tr(feature.title)}>
                  <div className="feature-icon">{feature.icon}</div>
                  <span className="feature-index">0{index + 1}</span>
                  <span className="eyebrow">{tr(feature.tag)}</span>
                  <h3>{tr(feature.title)}</h3>
                  <p>{tr(feature.text)}</p>
                </article>
              ))}
            </div>
          </section>
          {!native && (
            <section className="pricing-section" id="tarifs">
              <div className="landing-shell">
                <div className="section-heading centered">
                  <span className="eyebrow">{tr("CLAIR DÈS LE DÉPART")}</span>
                  <h2>
                     {tr("L’ambition pour tous.")} <br />
                    <span>{tr("Le bon plan pour chacun.")}</span>
                  </h2>
                  <p>{tr("Choisissez votre côté du terrain. On s’occupe du lien.")}</p>
                </div>
                <div className="pricing-grid">
                  <article className="price-card">
                    <div className="price-label">
                      <SportsScore />  {tr("POUR LES SPORTIFS")} </div>
                    <h3>{tr("Votre progression commence ici.")}</h3>
                    <div className="price">
                      0 €<span>{tr("/ pour votre compte")}</span>
                    </div>
                    <p>{tr("Tous les outils pour avancer avec votre coach.")}</p>
                    <ul>
                      {[
                        tr("Recherche de coachs et accès aux profils"),
                        tr("Réservation de séances et messagerie"),
                        tr("Suivi des performances et historique"),
                      ].map((item) => (
                        <li key={item}>
                          <Check />
                          {tr(item)}
                        </li>
                      ))}
                    </ul>
                    <button
                      className="landing-button secondary full-width"
                      onClick={() => register("SPORTIF")}
                    >
                       {tr("Créer mon compte sportif")} <ArrowForward />
                    </button>
                    <small>
                       {tr("Les séances et programmes sont réglés auprès du coach.")} </small>
                  </article>
                  <article className="price-card coach-price">
                    <div className="price-label">
                      <People />  {tr("POUR LES COACHS")} <span>{tr("6 MOIS OFFERTS")}</span>
                    </div>
                    <h3>{tr("Concentrez-vous sur l’essentiel.")}</h3>
                    <div className="price">
                      19,90 €<span>{tr("/ mois après l’essai")}</span>
                    </div>
                    <p>{tr("Un espace complet pour votre activité de coaching.")}</p>
                    <ul>
                      {[
                        tr("Profil professionnel et sportifs illimités"),
                        tr("Planning, programmes et évaluations"),
                        tr("Invitations par email et messagerie"),
                      ].map((item) => (
                        <li key={item}>
                          <Check />
                          {tr(item)}
                        </li>
                      ))}
                    </ul>
                    <button
                      className="landing-button full-width"
                      onClick={() => register("COACH")}
                    >
                       {tr("Démarrer mes 6 mois offerts")} <ArrowForward />
                    </button>
                    <small>{tr("6 mois gratuits, puis 19,90 € par mois.")}</small>
                  </article>
                </div>
              </div>
            </section>
          )}
          <section
            className="landing-shell section-space faq-section"
            id="questions"
          >
            <div>
              <span className="eyebrow">{tr("AVANT LE COUP D’ENVOI")}</span>
              <h2>
                 {tr("On répond")} <br />
                <span>{tr("à vos questions.")}</span>
              </h2>
              <p>{tr("Un autre sujet ? Parlons-en.")}</p>
              <a className="text-link" href="mailto:contact@g1sport.fr">
                contact@g1sport.fr <ArrowOutward fontSize="small" />
              </a>
            </div>
            <div className="faq-list">
              {[
                {
                  question: tr("G1Sport, c’est pour quel niveau ?"),
                  answer:
                    tr("Pour tous les niveaux. Que vous repreniez le sport ou cherchiez à améliorer vos performances, vous pouvez consulter les spécialités et les profils des coachs pour choisir votre accompagnement."),
                },
                {
                  question: tr("Comment réserver ma première séance ?"),
                  answer:
                    tr("Créez un compte sportif, choisissez un coach puis un créneau disponible. Vous pouvez sélectionner un programme ou demander une séance libre. Retrouvez ensuite le statut de votre demande dans votre espace."),
                },
                {
                  question:
                    tr("Puis-je découvrir la plateforme avant de m’inscrire ?"),
                  answer:
                    tr("Oui. Le bouton « Accès visiteur » ouvre un espace vide, sans compte ni données personnelles. Découvrez les univers sportifs librement. Pour réserver ou créer une séance, inscrivez-vous en choisissant votre profil Sportif ou Coach."),
                },
                {
                  question: tr("Je suis coach : puis-je inviter mes sportifs ?"),
                  answer:
                    tr("Oui. Depuis votre espace coach, envoyez une invitation par email avec un message personnalisé. Vous pourrez ensuite gérer les séances et accompagner la progression de vos sportifs."),
                },
                ...(!native
                  ? [
                      {
                        question:
                          tr("Le compte gratuit inclut-il le prix des séances ?"),
                        answer:
                          tr("Non. Le compte sportif et les outils de la plateforme sont gratuits. Les séances et programmes ont leurs propres tarifs, définis par chaque coach ; le règlement se fait directement auprès de lui."),
                      },
                    ]
                  : []),
              ].map((faq) => (
                <details key={tr(faq.question)}>
                  <summary>
                    {tr(faq.question)}
                    <Add fontSize="small" />
                  </summary>
                  <p>{tr(faq.answer)}</p>
                </details>
              ))}
            </div>
          </section>
          {!native && <MobileDownload />}

          <section className="landing-shell final-cta">
            <div className="cta-track" aria-hidden="true" />
            <span className="eyebrow">{tr("VOTRE PROCHAINE SÉANCE CHANGE TOUT.")}</span>
            <h2>
               {tr("Le déclic, c’est vous.")} <br />
               {tr("La suite, c’est ensemble.")} </h2>
            <button className="landing-button black" onClick={() => register()}>
               {tr("Rejoindre G1Sport")} <ArrowOutward />
            </button>
            <span className="cta-bottom">
               {tr("ON SE RETROUVE SUR LE TERRAIN.")} <ArrowDownward fontSize="small" />
            </span>
          </section>
        </main>
        <footer className="landing-shell landing-footer">
          <div className="footer-top">
            <div>
              <Link
                to="/"
                className="landing-logo"
                aria-label="G1Sport, accueil"
              >
                <img
                  className="app-brand-logo"
                  src="/g1s-logo-3d.png"
                  alt=""
                  width="44"
                  height="56"
                />
                <span>
                  G1SPORT<span className="logo-period">.</span>
                </span>
              </Link>
              <p>
                 {tr("Connecter les ambitions.")} <br />
                 {tr("Faire grandir les performances.")} </p>
            </div>
            <nav aria-label="Liens de pied de page">
              {nav.map((item) => (
                <a href={item.href} key={item.href}>
                  {tr(item.label)}
                </a>
              ))}
            </nav>
            <div className="footer-contact">
              <span>{tr("ON GARDE LE CONTACT ?")}</span>
              <a href="mailto:contact@g1sport.fr">
                contact@g1sport.fr <East fontSize="small" />
              </a>
              <button className="text-link" onClick={openDemo}>
                 {tr("Découvrir la démo")} <ArrowOutward fontSize="small" />
              </button>
            </div>
          </div>
          <div className="footer-bottom">
<a href="https://devantiq.com" className="devantiq-footer-brand" aria-label="DevAntiq, créateur de G1Sport"><img src="/images/brands/devantiq.png" alt="DevAntiq" width="64" height="48" loading="lazy" /></a>
            <span>
              © {new Date().getFullYear()}  {tr("G1Sport · Tous droits réservés.")} </span>
            <nav aria-label={tr("Informations légales")}>
              {[
                { text: tr("Mentions légales"), to: "/mentions-legales" },
                { text: tr("Confidentialité"), to: "/confidentialite" },
                { text: tr("CGU / CGV"), to: "/cgv" },
                { text: tr("Cookies"), to: "/cookies" },
              ].map((item) => (
                <Link key={item.to} to={item.to}>
                  {tr(item.text)}
                </Link>
              ))}
            </nav>
          </div>

        </footer>
        <Drawer
          anchor="right"
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          PaperProps={{
            sx: {
              width: "min(340px, 90vw)",
              bgcolor: "#12161b",
              color: "#eef0f3",
              p: 3,
              pt: "calc(24px + env(safe-area-inset-top))",
            },
          }}
        >
          <div className="mobile-drawer">
            <div>
              <span className="landing-logo">
                <img
                  className="app-brand-logo"
                  src="/g1s-logo-3d.png"
                  alt=""
                  width="44"
                  height="56"
                />
                <span>G1SPORT.</span>
              </span>
              <IconButton
                aria-label={tr("Fermer le menu")}
                onClick={() => setMenuOpen(false)}
                sx={{ color: "inherit" }}
              >
                <Close />
              </IconButton>
            </div>
            <nav aria-label="Navigation mobile">
              {nav.map((item) => (
                <a
                  href={item.href}
                  key={item.href}
                  onClick={() => setMenuOpen(false)}
                >
                  {tr(item.label)}
                  <ArrowOutward />
                </a>
              ))}
            </nav>
            <Link to="/login" onClick={() => setMenuOpen(false)}>
               {tr("Connexion")} </Link>
            <button
              className="landing-button"
              onClick={() => {
                setMenuOpen(false);
                register();
              }}
            >
               {tr("Créer mon compte")} <ArrowForward />
            </button>
            <button
              className="text-link"
              onClick={() => {
                setMenuOpen(false);
                openDemo();
              }}
            >
               {tr("Accès visiteur")} <PlayArrow />
            </button>
          </div>
        </Drawer>

      </div>
    </MotionConfig>
  );
}

function ArrowUpRight() {
  return <ArrowOutward fontSize="small" />;
}
